import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef
} from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import GithubAuth from '@/components/GithubAuth'
import TwitterAuth from '@/components/TwitterAuth'
import { GithubAuthState } from '@/hooks/services/useGithubAuth'
import { TwitterAuthState } from '@/hooks/services/useTwitterAuth'
import { useUserInfo } from '@/hooks/services/useUserInfo'
import useSignAndLogin from '@/hooks/useSignAndLogin'
import {
  githubAuthStateAtom,
  twitterAuthStateAtom,
  userInfoAtom
} from '@/stores/services'

export type GlobalMethodProviderProps = {
  loginLoading: boolean
  signMessageWithLogin: () => Promise<
    { signature: string; signatureMessage: string } | undefined
  >
  refreshUserInfo: () => void
  openTwitterAuth: () => void
  openGithubAuth: () => void
  // closeTwitterAuth: () => void
  authTwitter: (callback?: (data: any) => void) => void
  authGithub: (callback?: (data: any) => void) => void
}

export const GlobalMethodProviderContext =
  createContext<GlobalMethodProviderProps>(
    undefined! as GlobalMethodProviderProps
  )

export const useGlobalMethodProviderContext = (): GlobalMethodProviderProps =>
  useContext<GlobalMethodProviderProps>(GlobalMethodProviderContext)

const GlobalMethodProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { loginLoading, signMessageWithLogin } = useSignAndLogin()
  const { mutate } = useUserInfo()
  const userInfo = useAtomValue(userInfoAtom)
  const setTwitterAuthState = useSetAtom(twitterAuthStateAtom)
  const setGithubAuthState = useSetAtom(githubAuthStateAtom)

  const twitterAuthRef = useRef<React.ComponentRef<typeof TwitterAuth>>(null)
  const githubAuthRef = useRef<React.ComponentRef<typeof GithubAuth>>(null)

  // const openTwitterAuth = useCallback(() => {
  //   if (loginStatus !== LoginStatus.LOGIN) return
  //   if (twitterAuthState === TwitterAuthState.UN_KNOWN) return

  //   twitterAuthRef.current?.open()
  // }, [loginStatus, twitterAuthState])

  // const openGithubAuth = useCallback(() => {
  //   if (loginStatus !== LoginStatus.LOGIN) return
  //   if (githubAuthState === GithubAuthState.UN_KNOWN) return
  //   githubAuthRef.current?.open()
  // }, [loginStatus, githubAuthState])

  const authTwitter = (callback?: (data: any) => void) => {
    return twitterAuthRef.current?.authTwitter(callback)
  }

  const authGithub = (callback?: (data: any) => void) => {
    return githubAuthRef.current?.authGithub(callback)
  }

  useEffect(() => {
    if (userInfo) {
      let twitterState, githubState
      if (userInfo.xid) {
        twitterState = TwitterAuthState.AUTHENTICATED
      } else {
        twitterState = TwitterAuthState.UNAUTHENTICATED
      }
      if (userInfo.gid) {
        githubState = GithubAuthState.AUTHENTICATED
      } else {
        githubState = GithubAuthState.UNAUTHENTICATED
      }
      setTwitterAuthState(twitterState)
      setGithubAuthState(githubState)
    } else {
      setTwitterAuthState(TwitterAuthState.UN_KNOWN)
      setGithubAuthState(GithubAuthState.UN_KNOWN)
    }
  }, [userInfo, setTwitterAuthState, setGithubAuthState])

  return (
    <GlobalMethodProviderContext.Provider
      value={{
        loginLoading,
        signMessageWithLogin,
        refreshUserInfo: () => {
          mutate()
        },
        openTwitterAuth: () => {
          twitterAuthRef.current?.open()
        },
        openGithubAuth: () => {
          githubAuthRef.current?.open()
        },
        // closeTwitterAuth: () => {
        //   twitterAuthRef.current?.close()
        // }
        authTwitter,
        authGithub
      }}
    >
      <TwitterAuth ref={twitterAuthRef} />
      <GithubAuth ref={githubAuthRef} />
      {children}
    </GlobalMethodProviderContext.Provider>
  )
}

export default GlobalMethodProvider
